import React, { useEffect, useState } from 'react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { MrfDialog } from './MrfDialog';
import CloseIcon from '@mui/icons-material/Close';
import { Link } from "react-router-dom";
import { useData } from "@pure-ptr/react";
import { getPublishMRFUrls } from "../../../../store/api/pricers";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { asDate } from '../../../../util/date';
import CopyIcon from '@mui/icons-material/FileCopyOutlined';
import { infoToastify } from "../../../../util/toast";
import WarningIcon from "@mui/icons-material/Warning";

const useStyles = makeStyles(theme => ({
    button: {
        color: '#FFF',
        '&:hover': {
            color: '#FFF'
        }
    },
    mrfButton: {
        marginLeft: '0.5rem !important',
        padding: '5px !important',
        minWidth: '10px !important'
    }
}));

function PublishMRF(props) {
    const { isSuperAdmin, publishMRFloading, downloadMRFloading, mrfs, getNegotiatedRatesSignOffPending, 
        signOff, clientBrand, getDeploymentStatusPending, deploymentStatus,
        actions: { publishMRFs, getNegotiatedRatesSignOff, exportCsv, getBrand } } = props;

    const classes = useStyles();

    
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [mrfDialogData, setMrfDialogData] = useState([]);
    const [isMRFUrlDialogOpen, setMRFUrlDialogOpen] = useState(false);
    const [getPublishUrl, setGetPublishUrl] = useState(false);
    const [isURLCopied, setURLCopied] = useState(-1);
    const [isPublishedURLCopied, setPublishedURLCopied] = useState(-1);


    useEffect(() => {
        getBrand();
    }, [getBrand]);

    const { data: MRFUrls, isPending } = useData( async () => {
        const result = await getPublishMRFUrls();
        return result;
    }, [getPublishUrl]);

    useEffect(() => {
        getNegotiatedRatesSignOff();
    }, [getNegotiatedRatesSignOff]);

    function handlePublishMRFs() {
        publishMRFs(handleCallback);
    }
    function handleCallback() {
        getNegotiatedRatesSignOff();
        setGetPublishUrl(!getPublishUrl);
        setDialogOpen(true);
    }
    function handleDialogClose() {
        setDialogOpen(false);
    }
    function handleDownloadMRFs() {
        exportCsv(handleDownloadMRFsCallback);
    }
    function handleDownloadMRFsCallback() {
        infoToastify(
            <>
                <b>MRF File Download</b>
                <br />
                Your MRF file is being prepared. You’ll be notified once it is
                ready.
            </>,
        );
    }

    function handleOpenMRF() {
        if(MRFUrls.length > 0) {
            setMRFUrlDialogOpen(true);
        }
    }

    const handleURLCopyIcon = (facility, index) => {
        const url = document.getElementById(`url-${index}`).textContent;
        navigator.clipboard.writeText(url);
        setURLCopied(index);
        setTimeout(() => {
            setURLCopied(-1);
        }, 3000);
    }
    
    const handlePublishedURLCopyIcon = (facility, index) => {
        const publishUrl = document.getElementById(`published-url-${index}`).textContent;
        navigator.clipboard.writeText(publishUrl);
        setPublishedURLCopied(index);
        setTimeout(() => {
            setPublishedURLCopied(-1);
        }, 3000);
    }

    return (<>
        {isSuperAdmin &&
            <Grid container className='justify-content-end' sx={{mb:"20px"}}> 
                <grid item xs={2} className='text-right'>
                    <Tooltip title='Publish latest MRFs to update direct download links'>
                        <Button color='primary' disabled={publishMRFloading} variant='outlined' onClick={handlePublishMRFs}>
                            Publish Files
                            {publishMRFloading &&
                                <CircularProgress className='ml-2' color='primary' size={15} />
                            }
                        </Button>
                        
                    </Tooltip>
                    {MRFUrls && MRFUrls.length > 0 &&
                    <Tooltip title='Open Published MRF'>
                        <Button className={classes.mrfButton} color='primary' variant='outlined' onClick={handleOpenMRF}>
                            <OpenInNewIcon />
                        </Button>
                    </Tooltip>
                    }
                    <Tooltip title='Download latest published MRFs'>
                        <Button className='ml-2' color='primary' disabled={downloadMRFloading} variant='outlined' onClick={handleDownloadMRFs}>
                            Download Files
                            {downloadMRFloading &&
                                <CircularProgress className='ml-2' color='primary' size={15} />
                            }
                        </Button>
                    </Tooltip>
                </grid>
            </Grid>
        }
        <Dialog maxWidth='lg' fullWidth open={(isDialogOpen && !getNegotiatedRatesSignOffPending)}>
            <DialogTitle className='py-3'>
                <Grid container alignItems='center'>
                    <Grid item xs={11}>
                        <Typography variant="h6">
                            MRFs
                        </Typography>
                    </Grid>
                    <Grid item xs={1}>
                        <IconButton aria-label="close" onClick={handleDialogClose} size="large" className='pull-right'>
                            <CloseIcon />
                        </IconButton>
                    </Grid>
                </Grid>
            </DialogTitle>
            <DialogContent dividers>
                <Grid>
                    {mrfs.map((item, index) => <Grid spacing={2} container className='pt-1'>
                        <Grid item xs={2}>
                            <b>{item.name}</b>
                        </Grid>
                        <Grid item xs={4}>
                            <a id={`url-${index}`} href={item.url} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-word'}}>
                                {item.url}
                            </a>
                            <IconButton
                                className='p-1 ml-2'
                                aria-label="copy URL"
                                onClick={() => handleURLCopyIcon(item.name, index)}
                                color={isURLCopied === index ? 'secondary' : 'primary'}
                                size="large">
                                <CopyIcon />
                            </IconButton>
                            {isURLCopied === index && <span className='font-12 ml-2'>Copied</span>}
                        </Grid>
                        <Grid item xs={6}>
                            {item.message}
                            {item?.warnings && (
                                <Tooltip title={item?.warnings} arrow placement="top">
                                    <WarningIcon style={{ color: "red", marginLeft: 8, verticalAlign: "middle", cursor: "pointer" }} />
                                </Tooltip>
                            )}
                        </Grid>
                    </Grid>
                    )}
                    {(signOff && signOff.signedOff === 'false') && (
                        <>
                            <hr />
                            <Grid>
                                <Typography>A sign-off is required prior to publishing the MRF updates.</Typography>
                            </Grid>
                        </>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions className='p-4'>
                <Button variant="contained" onClick={handleDialogClose}>
                    Close
                </Button>
                {(signOff && signOff.signedOff === 'false') && (
                    <Button className={classes.button} component={Link} to="/pricer/brandconfiguration" variant="contained" color="primary">
                        Go to Sign off
                    </Button>
                )}
            </DialogActions>
        </Dialog>
        <MrfDialog open={mrfDialogData?.length > 0} data={mrfDialogData} handleCancel={()=>setMrfDialogData([])}/>
        <Dialog id='publish-mrf-dialog' maxWidth='md' fullWidth open={(isMRFUrlDialogOpen && !getNegotiatedRatesSignOffPending)}>
            <DialogTitle className='py-3'>
                <Grid container alignItems='center'>
                    <Grid item xs={11}>
                        <Typography variant="h6">
                            MRFs
                        </Typography>
                    </Grid>
                    <Grid item xs={1}>
                        <IconButton aria-label="close" onClick={()=>setMRFUrlDialogOpen(false)} size="large" className='pull-right'>
                            <CloseIcon />
                        </IconButton>
                    </Grid>
                </Grid>
            </DialogTitle>
            <DialogContent dividers>
                <Grid>
                    {MRFUrls && MRFUrls?.map((item, index) => <Grid id={item.name} spacing={2} container className='pt-1'>
                        <Grid item xs={4}>
                            <b>{item.name}</b>
                            {item?.warnings && (
                                <Tooltip title={item?.warnings} arrow placement="top">
                                    <WarningIcon style={{ color: "red", marginLeft: 8, verticalAlign: "middle", cursor: "pointer" }} />
                                </Tooltip>
                            )}
                        </Grid>
                        <Grid item xs={6}>
                            <a id={`published-url-${index}`} href={item.url} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-word'}}>
                                {item.url}
                            </a>
                            <IconButton
                                className='p-1 ml-2'
                                aria-label="copy published URL"
                                onClick={() => handlePublishedURLCopyIcon(item.name, index)}
                                color={isPublishedURLCopied === index ? 'secondary' : 'primary'}
                                size="large">
                                <CopyIcon />
                            </IconButton>
                            {isPublishedURLCopied === index && <span className='font-12 ml-2'>Copied</span>}
                        </Grid>
                        <Grid item xs={2}>
                            {asDate( new Date( item.publishDate ) )}
                        </Grid>
                    </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions className='p-4'>
                <Button variant="contained" onClick={()=>setMRFUrlDialogOpen(false)}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    </>);
}

export default PublishMRF
